import React from 'react';
import { toast } from 'sonner';
import { Tick, CloseCircle, DocumentText, Image, Trash } from '@/components/icons';
import FeatureUnavailableChip from '@/components/subscription/FeatureUnavailableChip';
import { Checkbox } from '@/components/ui/checkbox';
import CollapsibleSection from '@/components/ui/collapsible-section';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { useFeatureEntitlement } from '@/hooks/use-feature-entitlement';
import { getEntitlementBlockedMessage } from '@/lib/entitlements';
import { summarizeBrandAssets } from '../summarize';
import type { FileUploadState } from '../types';
import type { HeaderCustomizations } from '@/components/preview/types';

interface BrandAssetsSectionProps {
    open: boolean;
    onToggle: () => void;
    config: HeaderCustomizations;
    updateConfig: (updates: Partial<HeaderCustomizations>) => void;
    logoInputRef: React.RefObject<HTMLInputElement | null>;
    letterheadInputRef: React.RefObject<HTMLInputElement | null>;
    logoUploadState: FileUploadState;
    letterheadUploadState: FileUploadState;
    onFileUpload: (e: React.ChangeEvent<HTMLInputElement>, type: 'logo' | 'letterhead') => void;
    onDeleteImage: (type: 'logo' | 'letterhead') => void;
}

/**
 * Logo and letterhead are brand, not header layout, so they sit under "Sua
 * marca". The fields themselves still live in `customizations.header` — only
 * their place on screen moved.
 */
export function BrandAssetsSection({
    open,
    onToggle,
    config,
    updateConfig,
    logoInputRef,
    letterheadInputRef,
    logoUploadState,
    letterheadUploadState,
    onFileUpload,
    onDeleteImage,
}: BrandAssetsSectionProps) {
    const letterheadAccess = useFeatureEntitlement('reports.letterhead');

    return (
        <CollapsibleSection
            title="Logo e papel timbrado"
            testId="customize-section-brand"
            icon={Image}
            summary={summarizeBrandAssets(config)}
            open={open}
            onToggle={onToggle}
            contentClassName="space-y-2"
        >
            {/* Logo */}
            <div className="flex items-start gap-3 rounded-lg border border-border p-3 transition-colors hover:bg-surface">
                <Checkbox
                    id="show-logo"
                    checked={config.show_logo || false}
                    onCheckedChange={(checked) => updateConfig({ show_logo: !!checked })}
                    className="mt-0.5"
                />
                <div className="flex-1">
                    <Label
                        htmlFor="show-logo"
                        className="flex cursor-pointer items-center gap-2 text-sm font-medium"
                    >
                        <Image className="size-4 text-muted-foreground" />
                        Logo
                    </Label>
                    {config.show_logo && (
                        <div className="mt-2 space-y-2">
                            <Input
                                ref={logoInputRef}
                                type="file"
                                accept="image/jpeg,image/png,image/webp"
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                    onFileUpload(e, 'logo')
                                }
                                disabled={logoUploadState.status === 'uploading'}
                                className="cursor-pointer text-xs"
                            />

                            {logoUploadState.status === 'uploading' && (
                                <div className="space-y-1">
                                    <Progress value={logoUploadState.progress} className="h-1.5" />
                                    <p className="text-right text-[10px] text-muted-foreground">
                                        {logoUploadState.progress}%
                                    </p>
                                </div>
                            )}

                            {logoUploadState.status === 'error' && (
                                <p className="flex items-center gap-1 text-[10px] text-red-600">
                                    <CloseCircle className="size-3" /> {logoUploadState.error}
                                </p>
                            )}

                            {config.logo_url &&
                                logoUploadState.status !== 'uploading' &&
                                logoUploadState.status !== 'error' && (
                                    <div className="flex items-center justify-between">
                                        <p className="flex items-center gap-1 text-[10px] text-green-600">
                                            <Tick className="size-3" /> Arquivo carregado
                                        </p>
                                        <button
                                            onClick={() => onDeleteImage('logo')}
                                            className="p-1 text-red-500 transition-colors hover:text-red-700"
                                            title="Remover logo"
                                            type="button"
                                        >
                                            <Trash className="size-3" />
                                        </button>
                                    </div>
                                )}

                            <div className="mt-4 space-y-2">
                                <div className="grid grid-cols-3 gap-2">
                                    {(['small', 'medium', 'large'] as const).map((size) => (
                                        <button
                                            key={size}
                                            onClick={() =>
                                                updateConfig({
                                                    logo_size: size,
                                                })
                                            }
                                            className={`flex flex-col items-center gap-2 rounded-lg border-2 px-3 py-2 transition-all ${
                                                (config.logo_size || 'medium') === size
                                                    ? 'border-[#00006B] bg-[#00006B] text-white'
                                                    : 'border-border bg-card text-foreground hover:border-border-strong'
                                            }`}
                                        >
                                            <span className="text-xs font-medium">
                                                {size === 'small'
                                                    ? 'Pequeno'
                                                    : size === 'medium'
                                                      ? 'Médio'
                                                      : 'Grande'}
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Letterhead */}
            <div className="flex items-start gap-3 rounded-lg border border-border p-3 transition-colors hover:bg-surface">
                <Checkbox
                    id="show-letterhead"
                    checked={config.show_letterhead || false}
                    aria-disabled={letterheadAccess.shouldBlock}
                    onCheckedChange={(checked) => {
                        if (letterheadAccess.shouldBlock) {
                            toast.error(getEntitlementBlockedMessage(letterheadAccess.decision));
                            return;
                        }

                        updateConfig({
                            show_letterhead: !!checked,
                        });
                    }}
                    className="mt-0.5"
                />
                <div className="flex-1">
                    <Label
                        htmlFor="show-letterhead"
                        className="flex cursor-pointer items-center gap-2 text-sm font-medium"
                    >
                        <DocumentText className="size-4 text-muted-foreground" />
                        Papel Timbrado
                        {letterheadAccess.isUnavailableInPlan &&
                        letterheadAccess.availableFromPlan ? (
                            <FeatureUnavailableChip
                                availablePlan={letterheadAccess.availableFromPlan}
                            />
                        ) : null}
                    </Label>
                    {config.show_letterhead && (
                        <div className="mt-2 space-y-2">
                            <Input
                                ref={letterheadInputRef}
                                type="file"
                                accept="image/jpeg,image/png,image/webp"
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                    onFileUpload(e, 'letterhead')
                                }
                                disabled={
                                    letterheadUploadState.status === 'uploading' ||
                                    letterheadAccess.shouldBlock
                                }
                                className="cursor-pointer text-xs"
                            />

                            {letterheadUploadState.status === 'uploading' && (
                                <div className="space-y-1">
                                    <Progress
                                        value={letterheadUploadState.progress}
                                        className="h-1.5"
                                    />
                                    <p className="text-right text-[10px] text-muted-foreground">
                                        {letterheadUploadState.progress}%
                                    </p>
                                </div>
                            )}

                            {letterheadUploadState.status === 'error' && (
                                <p className="flex items-center gap-1 text-[10px] text-red-600">
                                    <CloseCircle className="size-3" /> {letterheadUploadState.error}
                                </p>
                            )}

                            {config.letterhead_url &&
                                letterheadUploadState.status !== 'uploading' &&
                                letterheadUploadState.status !== 'error' && (
                                    <div className="flex items-center justify-between">
                                        <p className="flex items-center gap-1 text-[10px] text-green-600">
                                            <Tick className="size-3" /> Arquivo carregado
                                        </p>
                                        <button
                                            onClick={() => onDeleteImage('letterhead')}
                                            className="p-1 text-red-500 transition-colors hover:text-red-700"
                                            title="Remover papel timbrado"
                                            type="button"
                                        >
                                            <Trash className="size-3" />
                                        </button>
                                    </div>
                                )}
                        </div>
                    )}
                </div>
            </div>
        </CollapsibleSection>
    );
}
